/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faHandshake, 
  faComments,
  faArrowLeft,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../config/api';
import { formatEmployerName } from '../utils/nameFormatter';
import '../styles/Companies.css';
import '../styles/Communities.css';

const Associations = () => {
  const navigate = useNavigate();
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [blueCompany, setBlueCompany] = useState(null);

  useEffect(() => {
    const loadAssociations = async () => {
      try {
        setLoading(true);
        const response = await axios.get(buildApiUrl('/api/companies/list'));
        const allCompanies = response.data?.companies || [];
        
        // Filter associations (exclude Baps - it's a community, shown on community page)
        const filtered = allCompanies.filter(company => {
          if ((company.section || '').toLowerCase() !== 'associations') return false;
          const name = (company.companyName || company.associationName || '').toLowerCase();
          if (name.includes('baps')) return false;
          return true;
        });
        setAssociations(filtered);

        // Find Blue Company - always include it
        const blueCompanyData = allCompanies.find(company => 
          company.companyName?.toLowerCase().includes('blue company')
        );
        setBlueCompany(blueCompanyData || null);
      } catch (error) {
        console.error('❌ Error fetching associations:', error);
        setAssociations([]);
        setBlueCompany(null);
      } finally {
        setLoading(false);
      }
    };

    loadAssociations();
  }, []);

  const stats = useMemo(() => {
    // Count unique partners: associations from API + Blue Company only if not already in list
    const blueCompanyInList = blueCompany && associations.some((a) => a._id === blueCompany._id);
    const totalPartners = associations.length + (blueCompany && !blueCompanyInList ? 1 : 0);
    const totalDiscussions = associations.reduce((count, association) => association.focusAreas ? count + 1 : count, 0);
    const totalCollaborations = associations.reduce((count, association) => association.memberCount ? count + 1 : count, 0);
    
    return {
      partners: totalPartners,
      discussions: totalDiscussions,
      collaborations: totalCollaborations
    };
  }, [associations, blueCompany]);

  const getInitials = (name) => {
    if (!name) return 'A';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getCompanyLogoUrl = (company) => {
    const raw = company?.companyLogo || company?.associationLogo || company?.logo || company?.logoUrl || company?.logo_url;
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
    const normalized = raw.replace(/\\/g, '/');
    if (normalized.startsWith('/uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace(/^\/uploads\//, '')}`);
    }
    if (normalized.startsWith('uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace(/^uploads\//, '')}`);
    }
    return buildApiUrl(`/api/users/uploads/${normalized}`);
  };

  return (
    <div className="companies-page communities-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faUsers} className="badge-icon" />
            <span>ASSOCIATION NETWORK</span>
          </div>
          <h1 className="companies-hero-title">
            Explore Vibrant Associations <br />
            <span className="gradient-text">Building Connections Together</span>
          </h1>
          <p className="companies-hero-subtitle">
            Join dynamic professional associations and organizations actively engaging to create meaningful career opportunities and drive social impact
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.partners}+</div>
                <div className="stat-label">Partner Associations</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faComments} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.discussions}+</div>
                <div className="stat-label">Active Discussions</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faHandshake} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.collaborations}+</div>
                <div className="stat-label">Collaborations</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      {/* Association Partners Section */}
      <section className="companies-container" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
        <div style={{ marginBottom: '2rem', maxWidth: '760px', margin: '0 auto 2rem auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', textAlign: 'center' }}>
            Our Association Partners
          </h2>
          <p style={{ color: '#4b5563', fontSize: '16px', margin: 0, textAlign: 'center' }}>
            Professional associations and cultural organizations partnering with Akshar Jobs.
          </p>
        </div>

        {loading ? (
          <div className="companies-loading">
            <div className="loading-spinner"></div>
            <p>Loading associations...</p>
          </div>
        ) : (
          <div className="companies-grid">
            {/* Blue Company - Featured only if not already in associations list (avoid duplicate) */}
            {blueCompany && !associations.some((a) => a._id === blueCompany._id) && (
              <div className="company-card" key="blue-company">
                <div className="company-card-header">
                  <div className="card-header-bg"></div>
                  <div className="company-logo-wrapper">
                    {getCompanyLogoUrl(blueCompany) ? (
                      <img
                        src={getCompanyLogoUrl(blueCompany)}
                        alt={formatEmployerName(blueCompany.companyName || 'Blue Company')}
                        className="company-logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) {
                            placeholder.style.display = 'none';
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className="company-logo-placeholder"
                      style={{ display: getCompanyLogoUrl(blueCompany) ? 'none' : 'flex' }}
                    >
                      <span className="initials-text">
                        {getInitials(formatEmployerName(blueCompany.companyName || 'Blue Company'))}
                      </span>
                    </div>
                  </div>
                  <div className="verified-badge">
                    <FontAwesomeIcon icon={faHandshake} />
                    <span>Verified</span>
                  </div>
                </div>

                <div className="company-card-body">
                  <h3 className="company-name" style={{ textAlign: 'center' }}>
                    {formatEmployerName(blueCompany.companyName || 'Blue Company')}
                  </h3>
                  {blueCompany.country && (
                    <div className="company-detail" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      <span style={{ flex: 'none' }}>{blueCompany.country}</span>
                    </div>
                  )}
                </div>

              </div>
            )}

            {associations.map((association, index) => {
              const rawName = association.companyName || association.associationName || association.employerName || 'Association';
              const name = formatEmployerName(rawName);

              return (
                <div className="company-card" key={association._id || association.id || index}>
                  <div className="company-card-header">
                    <div className="card-header-bg"></div>
                    <div className="company-logo-wrapper">
                      {getCompanyLogoUrl(association) ? (
                        <img
                          src={getCompanyLogoUrl(association)}
                          alt={name}
                          className="company-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                          onLoad={(e) => {
                            const placeholder = e.target.nextElementSibling;
                            if (placeholder) {
                              placeholder.style.display = 'none';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className="company-logo-placeholder"
                        style={{ display: getCompanyLogoUrl(association) ? 'none' : 'flex' }}
                      >
                        <span className="initials-text">{getInitials(name)}</span>
                      </div>
                    </div>
                    <div className="verified-badge">
                      <FontAwesomeIcon icon={faHandshake} />
                      <span>{rawName && (rawName.toLowerCase().includes('united asian network') || rawName.toLowerCase().includes('uan')) ? 'Verified' : 'Association Partner'}</span>
                    </div>
                  </div>

                  <div className="company-card-body">
                    <h3 className="company-name" style={{ textAlign: 'center' }}>{name}</h3>
                    {association.country && (
                      <div className="company-detail" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span style={{ flex: 'none' }}>{association.country}</span>
                      </div>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </section>

    </div>
  );
};

export default Associations;
